import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError, formatCurrency, buildAssetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { PageRefreshButton } from "@/components/PageRefreshButton";
// Phase M + N — invoice row tinting: same rules as Jobs plus a Phase N
// "not sent yet" bucket that overrides payment status so the manager
// spots un-dispatched invoices first (send-me signal beats collect-me).
const _PAYMENT_TINT_CLOSED = new Set(["write_off", "unable_to_collect", "cancelled", "voided"]);
function paymentTintBucket(status) {
  const s = (status || "").toLowerCase();
  if (s === "paid") return "paid";
  if (_PAYMENT_TINT_CLOSED.has(s)) return "closed";
  return "unpaid";
}
function invoiceSendBucket(inv) {
  const status = (inv?.status || "").toLowerCase();
  // Closed states never wear the yellow "action needed" tint.
  if (_PAYMENT_TINT_CLOSED.has(status)) return "sent";
  // Drafts (Phase 61.1 pill: no invoice_number OR status="draft") are
  // by definition un-sent; treat them as top-of-mind yellow.
  if (!inv?.invoice_number || status === "draft") return "not_sent";
  // Issued invoices are "sent" only once the send flow stamped `sent_at`.
  return inv?.sent_at ? "sent" : "not_sent";
}
function invoiceTintBucket(inv) {
  // "Not sent" overrides payment-status colour per Phase N.
  if (invoiceSendBucket(inv) === "not_sent") return "not_sent";
  return paymentTintBucket(inv?.payment_status);
}
function invoiceRowTint(inv) {
  const bucket = invoiceTintBucket(inv);
  if (bucket === "not_sent") return "bg-amber-500/10 hover:bg-amber-500/15";
  if (bucket === "paid")     return "bg-emerald-500/10 hover:bg-emerald-500/15";
  if (bucket === "closed")   return "bg-slate-500/5 hover:bg-slate-500/10";
  return "bg-red-500/10 hover:bg-red-500/15";
}
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Filter, ArrowRight, FileText, X, Search } from "lucide-react";

const STATUS_LABEL = {
  awaiting_payment: "Awaiting",
  paid: "Paid",
  overdue: "Overdue",
  write_off: "Write-off",
  unable_to_collect: "Uncollectible",
};
const STATUS_CLS = {
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  overdue: "bg-rose-100 text-rose-800 border-rose-200",
  awaiting_payment: "bg-amber-100 text-amber-900 border-amber-200",
  write_off: "bg-zinc-200 text-zinc-700 border-zinc-300",
  unable_to_collect: "bg-zinc-200 text-zinc-700 border-zinc-300",
};

export default function Accounts() {
  const [params, setParams] = useSearchParams();
  // Phase P.32 — hydrate filter defaults from localStorage (24h TTL) so
  // page reload restores the last-used view. URL params still win when
  // present so shareable links keep working.
  const _lsSnapshot = (() => {
    try {
      const raw = localStorage.getItem("jbk:accounts:filters");
      if (!raw) return null;
      const blob = JSON.parse(raw);
      if (!blob?.savedAt || Date.now() - Number(blob.savedAt) > 24 * 60 * 60 * 1000) {
        localStorage.removeItem("jbk:accounts:filters");
        return null;
      }
      return blob.filters || null;
    } catch { return null; }
  })();
  const [filters, setFilters] = useState({
    from: params.get("from") || _lsSnapshot?.from || "",
    to: params.get("to") || _lsSnapshot?.to || "",
    payment_type: params.get("payment_type") || _lsSnapshot?.payment_type || "all",
    payment_status: params.get("payment_status") || _lsSnapshot?.payment_status || "all",
    paid_method: params.get("paid_method") || _lsSnapshot?.paid_method || "all",
    include_excluded_types:
      params.get("include_excluded_types") === "1"
      || !!(_lsSnapshot && _lsSnapshot.include_excluded_types),
    // Phase P.8 — inline search on Accounts. Server-side (uses
    // /api/invoices?q=…) so it composes with all existing filters.
    q: params.get("q") || _lsSnapshot?.q || "",
  });
  // Phase P.32 — write-through persistence on every filter change.
  useEffect(() => {
    try {
      localStorage.setItem("jbk:accounts:filters", JSON.stringify({
        savedAt: Date.now(), filters,
      }));
    } catch { /* ignore */ }
  }, [filters]);
  // Debounced mirror of `filters.q` so we don't hit the API per keystroke.
  const [debouncedQ, setDebouncedQ] = useState(filters.q);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q), 250);
    return () => clearTimeout(t);
  }, [filters.q]);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({ count: 0, subtotal: 0, tax: 0, total: 0, by_status: {}, by_paid_method: [], currency: "EUR" });
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paidMethods, setPaidMethods] = useState([]);
  const [loading, setLoading] = useState(true);

  const buildQuery = () => {
    const q = {};
    if (filters.from) q.from = filters.from;
    if (filters.to) q.to = filters.to;
    if (filters.payment_type && filters.payment_type !== "all") q.payment_type = filters.payment_type;
    if (filters.payment_status && filters.payment_status !== "all") q.payment_status = filters.payment_status;
    if (filters.paid_method && filters.paid_method !== "all") q.paid_method = filters.paid_method;
    if (filters.include_excluded_types) q.include_excluded_types = true;
    if (debouncedQ && debouncedQ.trim().length >= 2) q.q = debouncedQ.trim();
    return q;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [inv, sum, pt, pm] = await Promise.all([
        api.get("/invoices", { params: buildQuery() }),
        api.get("/accounts/summary", { params: buildQuery() }),
        api.get("/settings/payment-types"),
        api.get("/settings/paid-methods").catch(() => ({ data: [] })),
      ]);
      setInvoices(inv.data);
      setSummary(sum.data);
      setPaymentTypes(pt.data);
      setPaidMethods(pm.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filters.from, filters.to, filters.payment_type, filters.payment_status, filters.paid_method, filters.include_excluded_types, debouncedQ]); // eslint-disable-line react-hooks/exhaustive-deps

  const [exportOpen, setExportOpen] = useState(false);
  const [exportRange, setExportRange] = useState({ from: "", to: "" });
  const [exporting, setExporting] = useState(false);

  const openExport = () => {
    // Default to current filters if both set, otherwise sensible default = current month.
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
    setExportRange({
      from: filters.from || monthStart,
      to: filters.to || monthEnd,
    });
    setExportOpen(true);
  };

  const downloadCsv = async () => {
    if (!exportRange.from || !exportRange.to) {
      toast.error("Both From and To dates are required");
      return;
    }
    if (exportRange.from > exportRange.to) {
      toast.error("'From' date must be on or before 'To' date");
      return;
    }
    setExporting(true);
    try {
      const q = { from: exportRange.from, to: exportRange.to };
      if (filters.payment_type && filters.payment_type !== "all") q.payment_type = filters.payment_type;
      if (filters.payment_status && filters.payment_status !== "all") q.payment_status = filters.payment_status;
      if (filters.paid_method && filters.paid_method !== "all") q.paid_method = filters.paid_method;
      const res = await api.get("/accounts/export", { params: q, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `accounts_${exportRange.from}_${exportRange.to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
      setExportOpen(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page" data-testid="accounts-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// P&L · Invoices</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Accounts</h1>
        </div>
        <div className="flex items-center gap-2">
          <PageRefreshButton onRefresh={load} testId="page-refresh-btn" />
          <Button onClick={openExport} className="btn-cta gap-2" data-testid="export-csv-button">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        {[
          { label: "Invoices", value: summary.count },
          { label: "Subtotal", value: formatCurrency(summary.subtotal, summary.currency) },
          { label: "Tax", value: formatCurrency(summary.tax, summary.currency) },
          { label: "Total", value: formatCurrency(summary.total, summary.currency), accent: true },
        ].map((s) => (
          <div key={s.label} className={`bg-card border ${s.accent ? "border-accent" : "border-border"} rounded-[8px] p-4`} data-testid={`accounts-stat-${s.label.toLowerCase()}`}>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{s.label}</div>
            <div className={`mt-1 ${s.accent ? "text-2xl font-black" : "text-xl font-bold"}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Phase J — compact chip-style filter row.
          Inline labels, smaller heights, single horizontal flex-wrap.
          "Showing N" line + Clear-all link replace the old 6-col grid. */}
      <div className="bg-card border border-border rounded-[8px] p-3 mt-4 space-y-2" data-testid="accounts-filters">
        {/* Phase P.8 — inline free-text search. Server-side via
            /api/invoices?q=... so it composes cleanly with the existing
            date / status / paid-method filters (AND semantics). */}
        <div className="flex items-center gap-2" data-testid="filter-chip-search">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search invoice # · customer · phone · email · address…"
              className="pl-8 h-8 text-xs"
              data-testid="accounts-filter-search"
            />
          </div>
          {filters.q && (
            <button
              type="button"
              onClick={() => setFilters({ ...filters, q: "" })}
              className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              data-testid="accounts-filter-search-clear"
            >
              <X className="h-3 w-3" /> Clear
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5" data-testid="filter-chip-from">
            <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">From</span>
            <Input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} className="h-8 text-xs w-[140px]" data-testid="accounts-from-input" />
          </div>
          <div className="flex items-center gap-1.5" data-testid="filter-chip-to">
            <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">To</span>
            <Input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} className="h-8 text-xs w-[140px]" data-testid="accounts-to-input" />
          </div>
          {/* Phase P.9 — "All time" clears both date bounds. */}
          {(filters.from || filters.to) && (
            <button
              type="button"
              onClick={() => setFilters({ ...filters, from: "", to: "" })}
              className="h-8 px-2 rounded-md border border-border text-[11px] font-bold text-muted-foreground hover:bg-secondary"
              data-testid="accounts-date-clear"
              title="Remove date range — show every invoice across all time"
            >
              All time
            </button>
          )}
          <div className="h-5 w-px bg-border" aria-hidden />
          <div className="flex items-center gap-1.5" data-testid="filter-chip-payment-type">
            <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Type</span>
            <Select value={filters.payment_type} onValueChange={(v) => setFilters({ ...filters, payment_type: v })}>
              <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="accounts-pt-select"><SelectValue /></SelectTrigger>
              <SelectContent data-testid="filter-chip-payment-type-popover">
                <SelectItem value="all">All</SelectItem>
                {paymentTypes.map((p) => (
                  <SelectItem key={p.id} value={p.code}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1.5" data-testid="filter-chip-status">
            <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Status</span>
            <Select value={filters.payment_status} onValueChange={(v) => setFilters({ ...filters, payment_status: v })}>
              <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="accounts-status-select"><SelectValue /></SelectTrigger>
              <SelectContent data-testid="filter-chip-status-popover">
                <SelectItem value="all">All</SelectItem>
                {Object.entries(STATUS_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1.5" data-testid="filter-chip-paid-method">
            <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Paid</span>
            <Select value={filters.paid_method} onValueChange={(v) => setFilters({ ...filters, paid_method: v })}>
              <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="accounts-paid-method-select"><SelectValue /></SelectTrigger>
              <SelectContent data-testid="filter-chip-paid-method-popover">
                <SelectItem value="all">All</SelectItem>
                {paidMethods.map((p) => (
                  <SelectItem key={p.id} value={p.code} data-testid={`accounts-paid-method-option-${p.code}`}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            <Switch checked={filters.include_excluded_types} onCheckedChange={(v) => setFilters({ ...filters, include_excluded_types: v })} data-testid="accounts-include-excluded-switch" />
            <Label className="text-[10px] mono uppercase tracking-wider text-muted-foreground">Incl. excluded</Label>
          </div>
          {(filters.from || filters.to || filters.payment_type !== "all" || filters.payment_status !== "all" || filters.paid_method !== "all" || filters.include_excluded_types) && (
            <button
              type="button"
              onClick={() => {
                setFilters({ from: "", to: "", payment_type: "all", payment_status: "all", paid_method: "all", include_excluded_types: false, q: "" });
                try { localStorage.removeItem("jbk:accounts:filters"); } catch { /* ignore */ }
              }}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              data-testid="filter-clear-all"
            >
              <X className="h-3 w-3" /> Clear all
            </button>
          )}
        </div>
        <div className="text-[10px] mono uppercase tracking-wider text-muted-foreground" data-testid="accounts-filter-showing">
          Showing {invoices.length} invoice{invoices.length === 1 ? "" : "s"}{summary.count && summary.count !== invoices.length ? ` of ${summary.count}` : ""}
        </div>
      </div>

      {/* Funds by Method breakdown */}
      {summary.by_paid_method && summary.by_paid_method.length > 0 && (
        <div className="mt-4 bg-card border border-border rounded-[8px] p-4" data-testid="funds-by-method-card">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Funds by method (paid only)</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {summary.by_paid_method.map((m) => (
              <div key={m.code} className="bg-secondary/30 rounded-[6px] px-3 py-2" data-testid={`funds-by-method-${m.code}`}>
                <div className="text-xs font-bold">{m.name}</div>
                <div className="text-base mono tabular-nums font-extrabold">{formatCurrency(m.total, summary.currency)}</div>
                <div className="text-[11px] text-muted-foreground">{m.count} invoice{m.count === 1 ? "" : "s"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invoice list */}
      <div className="mt-6">
        {loading ? (
          <div className="text-muted-foreground text-sm">Loading…</div>
        ) : invoices.length === 0 ? (
          <div className="bg-card border border-dashed border-border rounded-[8px] p-10 text-center text-sm text-muted-foreground">
            <FileText className="h-6 w-6 mx-auto text-muted-foreground" />
            <div className="mt-2">No invoices match these filters.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {invoices.map((inv) => (
              <Link
                key={inv.id}
                to={`/invoices/${inv.id}`}
                className={`border rounded-[8px] p-4 flex items-center justify-between gap-4 transition-colors border-border hover:border-accent ${invoiceRowTint(inv)}`}
                data-testid={`invoice-row-${inv.id}`}
                data-payment-status={paymentTintBucket(inv.payment_status)}
                data-send-status={invoiceSendBucket(inv)}
              >
                <div className="min-w-0 flex-1 grid grid-cols-2 md:grid-cols-5 gap-3 items-center">
                  <div>
                    {/* Phase 61.1 — only TRUE drafts (no I-number) get the DRAFT pill +
                        "—" placeholder. Legacy status="draft" docs that already have
                        a number render as normal — they're sendable via the existing
                        flow. */}
                    {!inv.invoice_number ? (
                      <div className="flex items-center gap-1.5">
                        <span className="mono text-[10px] uppercase tracking-widest border bg-amber-100 text-amber-900 border-amber-300 px-1.5 py-0.5 rounded font-bold" data-testid={`invoice-draft-pill-${inv.id}`}>
                          DRAFT
                        </span>
                        <span className="mono text-[11px] text-muted-foreground">—</span>
                      </div>
                    ) : (
                      <div className="mono font-bold text-sm">{inv.invoice_number}</div>
                    )}
                    <div className="text-[11px] text-muted-foreground mono">{inv.booking_code}</div>
                  </div>
                  <div className="text-sm font-semibold truncate">{inv.customer_snapshot?.company_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {/* Phase 61.1 — show the booking date, not the (possibly-null) issued_at. */}
                    {((inv.booking_date || inv.issued_at) || "").slice(0, 10)}
                  </div>
                  <div className="text-xs">
                    <span className="mono text-[10px] uppercase tracking-widest border border-border px-1.5 py-0.5 rounded">
                      {inv.payment_type_name}
                    </span>
                  </div>
                  <div className="flex items-center justify-end gap-3">
                    <span className="font-bold mono text-sm tabular-nums">{formatCurrency(inv.total, inv.currency)}</span>
                    <span className={`mono text-[10px] uppercase tracking-widest border px-1.5 py-0.5 rounded ${STATUS_CLS[inv.payment_status] || ""}`}>
                      {STATUS_LABEL[inv.payment_status] || inv.payment_status}
                    </span>
                    {inv.paid_via === "stripe_online" && (
                      <span
                        className="mono text-[10px] uppercase tracking-widest border bg-sky-100 text-sky-800 border-sky-300 px-1.5 py-0.5 rounded"
                        data-testid={`invoice-paid-via-online-${inv.id}`}
                        title="Paid online via Stripe Checkout"
                      >
                        Online
                      </span>
                    )}
                    {inv.payment_status === "paid" && inv.paid_via && inv.paid_via !== "stripe_online" && (
                      <span
                        className="mono text-[10px] uppercase tracking-widest border bg-zinc-100 text-zinc-700 border-zinc-300 px-1.5 py-0.5 rounded"
                        data-testid={`invoice-paid-via-${inv.id}`}
                      >
                        {inv.paid_method_name || "Manual"}
                      </span>
                    )}
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Export CSV: required date range */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent data-testid="export-csv-dialog">
          <DialogHeader>
            <DialogTitle>Export accounts CSV</DialogTitle>
            <DialogDescription>
              Pick a date range. Only invoices issued within this window are exported.
              Excluded payment types are always omitted.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold">From *</Label>
              <Input
                type="date"
                value={exportRange.from}
                onChange={(e) => setExportRange((r) => ({ ...r, from: e.target.value }))}
                data-testid="export-from-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold">To *</Label>
              <Input
                type="date"
                value={exportRange.to}
                onChange={(e) => setExportRange((r) => ({ ...r, to: e.target.value }))}
                data-testid="export-to-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)}>Cancel</Button>
            <Button
              onClick={downloadCsv}
              disabled={!exportRange.from || !exportRange.to || exporting}
              className="btn-cta"
              data-testid="export-confirm-button"
            >
              {exporting ? "Exporting…" : "Download"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wider font-semibold">{label}</Label>
      {children}
    </div>
  );
}
